import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAuthSession } from '../util/amplifyAuth';
import { listGrantedToMe, redeemInvite } from '../components/API';
import { formatDateISO, getInitials } from '../util/GenericUtil';
import { useToast } from '../components/Toast/ToastProvider';
import useOrderedList from '../hooks/useOrderedList';
import UserHeader from '../components/UserHeader/UserHeader';
import Home from './Home';
import HelixLoader from '../components/HelixLoader/HelixLoader';
import '../styles/DoctorDashboard.css';

export default function PeerDashboard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { showToast } = useToast();

    const [connections, setConnections] = useState([]);
    const [selectedPeer, setSelectedPeer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const redeeming = useRef(false);
    const [userId, setUserId] = useState(null);

    const { searchQuery, setSearchQuery, displayedItems: displayedConnections, canDrag, dragProps } =
        useOrderedList(connections, { storageKey: 'bloodeye:peerOrder', userId });

    useEffect(() => {
        checkAuthAndLoad();
    }, []);

    async function checkAuthAndLoad() {
        try {
            const session = await fetchAuthSession();
            if (!session.tokens?.accessToken) {
                const inviteToken = searchParams.get('invite');
                const redirect = inviteToken
                    ? `/peer?invite=${inviteToken}`
                    : '/peer';
                navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
                return;
            }

            const sub = session.tokens?.idToken?.payload?.sub;
            if (sub) setUserId(sub);

            const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];
            if (groups.includes('Doctors')) {
                navigate('/doctor');
                return;
            }

            const inviteToken = searchParams.get('invite');
            if (inviteToken && !redeeming.current) {
                redeeming.current = true;
                try {
                    const result = await redeemInvite(inviteToken);
                    showToast(`You now have access to ${result.inviterName}'s data`, 'success');
                } catch (err) {
                    showToast(err.message || 'Failed to redeem invite', 'error');
                }
                const next = new URLSearchParams(searchParams);
                next.delete('invite');
                setSearchParams(next, { replace: true });
            }

            await loadConnections();
        } catch (err) {
            console.error('Auth check failed:', err);
            navigate('/login?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
        }
    }

    async function loadConnections() {
        try {
            setLoading(true);
            setError(null);

            const result = await listGrantedToMe();
            const peers = (result.patients || []).filter(p => (p.granteeType || 'doctor') === 'peer');
            setConnections(peers);
        } catch (err) {
            console.error('Error loading peer connections:', err);
            setError('Failed to load shared connections');
        } finally {
            setLoading(false);
        }
    }

    function handlePeerSelect(peer) {
        setSelectedPeer(peer);
    }

    function handleBackToList() {
        setSelectedPeer(null);
    }

    return (
        <div className="doctor-dashboard">
            <UserHeader
                sharingMode="peer"
                sharingLabel={selectedPeer ? (selectedPeer.patientName || 'Shared Data') : null}
                onBack={selectedPeer ? handleBackToList : null}
            >
                <a onClick={() => navigate('/home')} className="header-link">Home</a>
            </UserHeader>

            <div className="doctor-content">
                {/* Peer List Sidebar */}
                <aside className={`patient-list-sidebar ${selectedPeer ? 'collapsed' : ''}`}>
                    <div className="sidebar-header">
                        <h2>Shared With Me</h2>
                        <div className="sidebar-header-actions">
                            <button
                                className="btn-icon"
                                onClick={loadConnections}
                                title="Refresh connections"
                            >
                                ↻
                            </button>
                        </div>
                    </div>

                    <div className="sidebar-body">
                        {loading && (
                            <div className="loading-state">
                                <HelixLoader dotCount={15} gap={2} />
                            </div>
                        )}

                        {error && (
                            <div className="error-state">
                                <p>{error}</p>
                                <button onClick={loadConnections}>Try Again</button>
                            </div>
                        )}

                        {!loading && !error && connections.length > 0 && (
                            <div className="sidebar-search">
                                <input
                                    type="text"
                                    placeholder="Search peers..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                            </div>
                        )}

                        {!loading && !error && connections.length === 0 && (
                            <div className="empty-state">
                                <p>No one has shared their data with you yet.</p>
                                <p className="hint">When someone shares their results with you, they'll appear here.</p>
                            </div>
                        )}

                        {!loading && !error && connections.length > 0 && displayedConnections.length === 0 && (
                            <div className="empty-state">
                                <p>No peers matching "{searchQuery}"</p>
                            </div>
                        )}

                        {!loading && !error && displayedConnections.length > 0 && (
                            <ul className="patient-list">
                                {displayedConnections.map((peer, index) => (
                                    <li
                                        key={peer.patientId}
                                        className={`patient-card ${selectedPeer?.patientId === peer.patientId ? 'active' : ''}`}
                                        onClick={() => handlePeerSelect(peer)}
                                        {...dragProps(index)}
                                    >
                                        <div className="patient-card-header">
                                            <div className="patient-initials">
                                                {getInitials(peer.patientName)}
                                            </div>
                                            <div className="patient-info">
                                                <h3>{peer.patientName || 'User'}</h3>
                                                <p className="patient-id">
                                                    {peer.scope === 'selective' ? 'Selective access' : 'All files'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="patient-card-meta">
                                            <span className="meta-item">
                                                <span className="meta-label">Shared:</span>
                                                <span className="meta-value">{formatDateISO(peer.grantedAt)}</span>
                                            </span>
                                            {peer.expiresAt && (
                                                <span className="meta-item">
                                                    <span className="meta-label">Expires:</span>
                                                    <span className="meta-value expires">{formatDateISO(peer.expiresAt)}</span>
                                                </span>
                                            )}
                                        </div>
                                        <div className="patient-card-arrow">›</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </aside>

                {/* Peer View or Welcome */}
                {selectedPeer ? (
                    <div className="patient-view-body">
                        <div className="patient-view-content">
                            <Home
                                readOnly={true}
                                patientId={selectedPeer.patientId}
                                patientName={selectedPeer.patientName}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="doctor-welcome">
                        <div className="doctor-welcome-content">
                            <div className="doctor-welcome-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <path d="M20 8v6" />
                                    <path d="M23 11h-6" />
                                </svg>
                            </div>
                            <h2>Peer Sharing</h2>
                            <p>Select a peer from the sidebar to view their shared lab results and health scores.</p>
                            <div className="doctor-welcome-hints">
                                <div className="welcome-hint">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                                    </svg>
                                    <span>Compare trends and see how others optimize</span>
                                </div>
                                <div className="welcome-hint">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="16" x2="12" y2="12" />
                                        <line x1="12" y1="8" x2="12.01" y2="8" />
                                    </svg>
                                    <span>View shared data in read-only mode</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
